import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { RequireCapability } from '@/components/layout/RequireCapability';
import { ChallengeBoard } from '@/features/challenges/ChallengeBoard';
import { ChallengeDetail } from '@/features/challenges/ChallengeDetail';
import { ChallengeEditor } from '@/features/challenges/ChallengeEditor';
import { ShopInventory } from '@/features/shop/ShopInventory';
import { ShopItemDetail } from '@/features/shop/ShopItemDetail';
import { SurveyResults } from '@/features/whats-new/SurveyResults';

/**
 * Content Studio — shop, ads & challenges. The shop tab is fully implemented
 * against /api/shop; the other tabs are scoped stubs until their surfaces land.
 *
 * Tab and open item live in the URL so a "look at rage_cup" link pasted in
 * Slack opens the right screen, not the top of a list.
 */

const TABS = [
  { key: 'shop', label: 'Shop items' },
  { key: 'ads', label: 'Advertisements' },
  { key: 'challenges', label: 'Challenges' },
  { key: 'whats-new', label: "What's new" },
  { key: 'promotions', label: 'Promotions' },
  { key: 'taxonomy', label: 'Taxonomy' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const searchSchema = z.object({
  tab: z.enum(['shop', 'ads', 'challenges', 'whats-new', 'promotions', 'taxonomy']).default('shop'),
  item: z.string().optional(),
  /** Open challenge, by key. Separate from `item` so a shop link and a challenge link cannot collide. */
  challenge: z.string().optional(),
  /** 'new' to create, or a key to edit. In the URL so a half-filled form survives a refresh. */
  edit: z.string().optional(),
});

export const Route = createFileRoute('/content')({
  validateSearch: searchSchema,
  component: Screen,
});

function Screen() {
  const { tab, item, challenge, edit } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const goTab = (next: TabKey) => {
    void navigate({
      search: { tab: next, item: undefined, challenge: undefined, edit: undefined },
    });
  };
  const openItem = (itemId: string) => {
    void navigate({ search: { tab: 'shop', item: itemId } });
  };
  const closeItem = () => {
    void navigate({ search: { tab: 'shop', item: undefined } });
  };
  const openChallenge = (key: string) => {
    void navigate({ search: { tab: 'challenges', challenge: key } });
  };
  const closeChallenge = () => {
    void navigate({ search: { tab: 'challenges', challenge: undefined } });
  };
  const editChallenge = (key?: string) => {
    void navigate({ search: { tab: 'challenges', edit: key ?? 'new' } });
  };
  // A successful save lands on the challenge it wrote, so the operator sees the result rather
  // than an empty list they have to search.
  const closeEditor = (key?: string) => {
    void navigate({ search: { tab: 'challenges', edit: undefined, challenge: key } });
  };

  return (
    <RequireCapability capability="content.view">
      <div className="flex flex-col gap-4">
        <div className="max-w-[660px]">
          <div
            className="font-head text-tx3 text-[12px] font-semibold uppercase"
            style={{ letterSpacing: '0.2em' }}
          >
            Content Studio
          </div>
          <h1 className="font-head text-tx m-0 mt-1 text-[28px] leading-[1.05] font-semibold md:text-[34px]">
            Shop, ads & challenges
          </h1>
          <p className="text-tx2 mt-2 mb-0 text-[14.5px] leading-[1.6]">
            Almost everything here is reversible. The exceptions are publishing a limited
            edition and replacing art people already own — those are marked in red. There is no
            delete: items are retired, which pulls them off sale while every owner keeps theirs.
          </p>
        </div>

        <div className="border-bd bg-bg-alt overflow-x-auto rounded-lg border">
          <div className="flex min-w-max">
            {TABS.map(({ key, label }) => {
              const active = key === tab;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => goTab(key)}
                  className="font-head hover:text-tx relative min-h-11.5 px-4 text-[15px] font-semibold whitespace-nowrap uppercase"
                  style={{
                    letterSpacing: '0.09em',
                    background: active ? 'var(--card)' : 'transparent',
                    color: active ? 'var(--tx)' : 'var(--tx3)',
                  }}
                >
                  {label}
                  <span
                    className="absolute inset-x-0 bottom-0 h-0.5"
                    style={{ background: active ? 'var(--brand)' : 'transparent' }}
                  />
                </button>
              );
            })}
          </div>
        </div>

        {tab === 'shop' ? (
          item ? (
            <ShopItemDetail itemId={item} onBack={closeItem} />
          ) : (
            <ShopInventory onOpenItem={openItem} />
          )
        ) : tab === 'challenges' ? (
          edit ? (
            <ChallengeEditor
              key={edit}
              challengeKey={edit === 'new' ? undefined : edit}
              onDone={closeEditor}
            />
          ) : challenge ? (
            <ChallengeDetail
              challengeKey={challenge}
              onBack={closeChallenge}
              onEdit={editChallenge}
            />
          ) : (
            <ChallengeBoard onOpen={openChallenge} onCreate={() => editChallenge()} />
          )
        ) : tab === 'whats-new' ? (
          /* Read-only: cards are authored by ZazzBackend's scripts/seedWhatsNew.mjs, and this is
             the only place their survey answers can be read. */
          <SurveyResults />
        ) : (
          <div className="flex max-w-[560px] flex-col gap-2.5 py-1">
            <h2 className="font-head text-tx m-0 text-[24px] font-semibold">
              {TABS.find(({ key }) => key === tab)?.label}
            </h2>
            <p className="text-tx2 m-0 text-[13.5px] leading-[1.6]">
              Scoped and reachable. Shop items are live in the tab to the left; this surface
              lands in a later pass.
            </p>
          </div>
        )}
      </div>
    </RequireCapability>
  );
}
